/**
 * 5-Star Review Request — shared constants and pure builders
 * Used by CheckIn.js (schedules the send) and Settings.js (edits the templates)
 */

export const REVIEW_REQUEST_DEFAULT_SMS =
  "Hi {firstName}! Thank you for choosing {storeName} — it was a pleasure serving you today. " +
  "If you have a quick moment, we'd love a review. It means a lot to our small shop! ⭐\n\n{reviewLink}";
export const REVIEW_REQUEST_DEFAULT_EMAIL_SUBJECT = "How was your ride with {storeName}?";
export const REVIEW_REQUEST_DEFAULT_EMAIL_BODY =
  "Hi {firstName},\n\nThank you for choosing {storeName} — it was a pleasure serving you today. " +
  "If you have a quick moment, we'd love a review. It means a lot to our small shop!";
export const REVIEW_REQUEST_OPTOUT_SUFFIX = "Reply STOP to opt out.";

/**
 * Replace the four supported wildcards in a template string.
 * @param {string} str
 * @param {{firstName?: string, lastName?: string, storeName?: string, reviewLink?: string}} vars
 * @returns {string}
 */
export const resolveReviewWildcards = (str, vars = {}) => {
  return (str || "")
    .replace(/{firstName}/g, vars.firstName || "")
    .replace(/{lastName}/g, vars.lastName || "")
    .replace(/{storeName}/g, vars.storeName || "")
    .replace(/{reviewLink}/g, vars.reviewLink || "");
};

// Render plain-text (with \n line breaks) into the inline-styled wrapper used elsewhere
// in this app for transactional-style mail (see sendCompletionMessages in CheckIn.js),
// with a centered "Leave a Review" button.
const buildReviewEmailHtml = (body, { storeName, reviewLink }) => {
  const paragraphs = (body || "")
    .split(/\n\n+/)
    .map((block) =>
      `<p style="font-size: 15px; color: #333; line-height: 22px; margin-bottom: 16px;">${block.replace(/\n/g, "<br>")}</p>`
    )
    .join("");

  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      ${paragraphs}
      <div style="text-align: center; margin: 30px 0;">
        <a href="${reviewLink}" style="background-color: #1F8A5B; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block;">
          ⭐ Leave a Review
        </a>
      </div>
      <p style="color: #666; font-size: 14px;">Thank you for choosing ${storeName}!</p>
    </div>
  `;
};

/**
 * Build the fully-resolved SMS/email payloads for a review request, or null when
 * there's no URL configured or no contact info to send to.
 * @param {object} booking
 * @param {object} settings - the full Zustand `settings` object (settings.reviewRequest holds this feature's doc)
 * @returns {{ phone: string, email: string, smsBody: string, emailSubject: string, emailHtml: string } | null}
 */
export const buildReviewRequestPayloads = (booking, settings) => {
  const rr = settings?.reviewRequest || {};
  const url = (rr.url || "").trim();
  if (!url) return null;

  const phone = (booking?.customerPhone || booking?.phone || "").replace(/\D/g, "");
  const cleanPhone = phone.length === 10 ? phone : "";
  const email = (booking?.customerEmail || booking?.email || "").trim();

  if (!cleanPhone && !email) return null;

  const storeName = settings?.storeName?.name || "We";
  const wildcardVars = {
    firstName: booking?.firstName || "",
    lastName: booking?.lastName || "",
    storeName,
    reviewLink: url,
  };

  let smsBody = "";
  if (cleanPhone) {
    const smsTemplate = rr.smsTemplate || REVIEW_REQUEST_DEFAULT_SMS;
    let resolved = resolveReviewWildcards(smsTemplate, wildcardVars);
    if (!smsTemplate.includes("{reviewLink}")) {
      resolved = `${resolved}\n\n${url}`;
    }
    smsBody = `${resolved}\n\n${REVIEW_REQUEST_OPTOUT_SUFFIX}`;
  }

  let emailSubject = "";
  let emailHtml = "";
  if (email) {
    const subjectTemplate = rr.emailSubject || REVIEW_REQUEST_DEFAULT_EMAIL_SUBJECT;
    const bodyTemplate = rr.emailBody || REVIEW_REQUEST_DEFAULT_EMAIL_BODY;
    emailSubject = resolveReviewWildcards(subjectTemplate, wildcardVars);
    const resolvedBody = resolveReviewWildcards(bodyTemplate, wildcardVars);
    emailHtml = buildReviewEmailHtml(resolvedBody, { storeName, reviewLink: url });
  }

  return {
    phone: cleanPhone,
    email,
    smsBody,
    emailSubject,
    emailHtml,
  };
};
